import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import TeamScoresPage from './TeamScoresPage';
import ChatterPage from './ChatterPage';
import ChatterNewPage from './ChatterNewPage';
import CompanyPage from './CompanyPage';
import CadencePage from './CadencePage';
import PodiumPage from './PodiumPage';
import AtlasPage from './AtlasPage';
import BriefsPage from './BriefsPage';
import FolioPage from './FolioPage';
import ResearchOSHome from './ResearchOSHome';
import { useAuth } from '../../context/AuthContext';

// ResearchOS — a first-class platform (reached from its own dashboard portal). The landing (ResearchOSHome) is
// search-first, then one live tile per module. This list only names the sections for the header breadcrumb; which
// tiles a user sees is decided by the landing's endpoint (the same gates as each module's page).
const GOLD = 'rgb(var(--brand-gold-rgb))';   // theme-linked: blue in light, gold in dark (matches app)

const SECTIONS = [
  { path: '/researchos/scores', label: 'Quorum' },
  { path: '/researchos/chatter-new', label: 'Chatter' },
  { path: '/researchos/chatter', label: 'Chatter', adminLabel: 'Chatter · classic' },
  { path: '/researchos/podium', label: 'Podium', superuserOnly: true },
  { path: '/researchos/cadence', label: 'Cadence', superuserOnly: true },   // backend also gates (superuser)
  { path: '/researchos/atlas', label: 'Atlas', superuserOnly: true },
  { path: '/researchos/briefs', label: 'Courier', superuserOnly: true },
  { path: '/researchos/folio', label: 'Folio', superuserOnly: true },   // fundamentals; backend also gates (superuser)
  { path: '/researchos/company', label: 'Company' },
];

// the old review page's address: the review now lives in Folio's Review tab (the company in the address is kept)
function ToFolioReview() {
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);
  const next = new URLSearchParams({ tab: 'review' });
  if (q.get('ticker')) next.set('ticker', q.get('ticker'));
  return <Navigate to={`/researchos/folio?${next.toString()}`} replace />;
}

function visibleSections(user) {
  return SECTIONS.filter((s) => !s.superuserOnly || user?.is_admin);
}

export default function ResearchOSLayout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const path = loc.pathname.replace(/\/$/, '') || '/researchos';
  const atHome = path === '/researchos';
  const section = visibleSections(user).find((s) => path.startsWith(s.path));

  return (
    <div className="mx-auto max-w-6xl py-4 sm:px-4 sm:py-6">{/* phones: the app shell already pads 16 px — a second px-4 left 311 of 375 px */}
      <header className="mb-4 flex items-center gap-2 text-sm sm:mb-5">
        <button onClick={() => nav('/researchos')}
          className="font-serif text-2xl text-gray-900 dark:text-gray-100" style={{ letterSpacing: '0.02em' }}>
          Research<span style={{ color: GOLD }}>OS</span>
        </button>
        {!atHome && section && (
          <>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <span className="text-gray-600 dark:text-gray-300">{(user?.is_admin && section.adminLabel) || section.label}</span>
          </>
        )}
        {atHome && (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-gray-400">
            {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
          </span>
        )}
      </header>

      <Routes>
        <Route index element={<ResearchOSHome isAdmin={!!user?.is_admin} />} />
        <Route path="scores" element={<TeamScoresPage />} />
        <Route path="chatter/*" element={<ChatterPage />} />
        <Route path="chatter-new" element={<ChatterNewPage />} />
        {/* Cadence: superuser-only for now. Non-superusers hitting the URL fall through to the
            catch-all redirect below (and the backend API 403s them regardless). */}
        {user?.is_admin && <Route path="cadence" element={<CadencePage />} />}
          {user?.is_admin && <Route path="podium/*" element={<PodiumPage />} />}
        {user?.is_admin && <Route path="atlas" element={<AtlasPage />} />}
        {user?.is_admin && <Route path="briefs" element={<BriefsPage />} />}
        {user?.is_admin && <Route path="folio" element={<FolioPage />} />}
        {user?.is_admin && <Route path="fundamentals" element={<ToFolioReview />} />}
        <Route path="company" element={<CompanyPage />} />
        <Route path="company/:ticker" element={<CompanyPage />} />
        {/* legacy path → new home for the YouTube pipeline */}
        <Route path="pipeline/*" element={<Navigate to="/researchos/chatter/youtube" replace />} />
        <Route path="*" element={<Navigate to="/researchos" replace />} />
      </Routes>
    </div>
  );
}
